import { useState, useEffect } from 'react';
import { InsightsIcon } from '../components/Icons';
import { fetchPortfolio, fetchIndices, fetchPortfolioSentiment } from '../utils/api';

function SentimentBadge({ sentiment }) {
  const config = {
    bullish: { color: '#00ff9d', bg: 'rgba(0,255,157,0.12)', label: 'Bullish' },
    bearish: { color: '#ff4d4d', bg: 'rgba(255,77,77,0.12)', label: 'Bearish' },
    neutral: { color: '#fbbf24', bg: 'rgba(251,191,36,0.12)', label: 'Neutral' },
  };
  const c = config[sentiment] || config.neutral;
  return (
    <span style={{
      display: 'inline-block',
      padding: '3px 10px',
      borderRadius: 6,
      fontSize: 12,
      fontWeight: 600,
      color: c.color,
      background: c.bg,
      border: `1px solid ${c.color}33`,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    }}>{c.label}</span>
  );
}

function SentimentBar({ score }) {
  const pct = ((score + 1) / 2) * 100;
  const color = score > 0.2 ? '#00ff9d' : score < -0.2 ? '#ff4d4d' : '#fbbf24';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
      <div style={{
        flex: 1,
        height: 6,
        borderRadius: 3,
        background: 'rgba(255,255,255,0.06)',
        overflow: 'hidden',
      }}>
        <div style={{
          width: `${pct}%`,
          height: '100%',
          borderRadius: 3,
          background: `linear-gradient(90deg, #ff4d4d 0%, #fbbf24 50%, #00ff9d 100%)`,
          transition: 'width 0.5s ease',
        }} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 600, color, minWidth: 40, textAlign: 'right' }}>
        {score > 0 ? '+' : ''}{score.toFixed(2)}
      </span>
    </div>
  );
}

function DailyInsights() {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(false);
  const [portfolio, setPortfolio] = useState([]);
  const [sentiment, setSentiment] = useState(null);
  const [sentimentLoading, setSentimentLoading] = useState(false);
  const [expandedTicker, setExpandedTicker] = useState(null);

  useEffect(() => { fetchPortfolio().then(setPortfolio); }, []);

  async function generateBrief() {
    setLoading(true);
    const indices = await fetchIndices();
    setBrief(indices);
    setLoading(false);
  }

  async function analyzeSentiment() {
    setSentimentLoading(true);
    try {
      const data = await fetchPortfolioSentiment();
      setSentiment(data);
    } catch {
      setSentiment({ error: true });
    }
    setSentimentLoading(false);
  }

  const tickers = portfolio.map(h => h.ticker);
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div>
      <div className="page-title">
        <InsightsIcon size={28} color="#00ff9d" />
        <h1>Daily Insights</h1>
      </div>
      <p className="hero-subtitle">Your personalized morning market brief — powered by real-time data</p>

      <div className="form-row" style={{ marginBottom: 16 }}>
        <button className="btn-primary" onClick={generateBrief} disabled={loading} style={{ flex: 1 }}>
          {loading ? <><span className="spinner" />Compiling brief...</> : 'Generate Market Brief'}
        </button>
        <button className="btn-primary" onClick={analyzeSentiment} disabled={sentimentLoading || portfolio.length === 0} style={{ flex: 1, background: 'linear-gradient(135deg, rgba(0,255,157,0.15), rgba(167,139,250,0.15))', border: '1px solid rgba(0,255,157,0.3)', color: '#f1f5f9' }}>
          {sentimentLoading ? <><span className="spinner" />Analyzing sentiment...</> : 'Analyze News Sentiment'}
        </button>
      </div>

      {sentiment && sentiment.error && (
        <div className="alert-box alert-info" style={{ marginBottom: 16 }}>
          Unable to fetch sentiment data. Please try again later.
        </div>
      )}

      {sentiment && !sentiment.error && (
        <div style={{ marginBottom: 24 }}>
          <div className="glass-card" style={{ marginBottom: 16, borderLeft: `3px solid ${sentiment.portfolio_sentiment === 'bullish' ? '#00ff9d' : sentiment.portfolio_sentiment === 'bearish' ? '#ff4d4d' : '#fbbf24'}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
              <div>
                <h3 style={{ margin: 0, color: '#f1f5f9' }}>Portfolio Sentiment Overview</h3>
                <p style={{ margin: '6px 0 0', color: '#94a3b8', fontSize: 14 }}>
                  AI-powered analysis of {sentiment.stocks.length} holding{sentiment.stocks.length !== 1 ? 's' : ''} across {sentiment.stocks.reduce((a, s) => a + s.analyzed_count, 0)} news articles
                </p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <SentimentBadge sentiment={sentiment.portfolio_sentiment} />
                <div style={{ marginTop: 6, fontSize: 22, fontWeight: 700, color: sentiment.portfolio_score > 0.2 ? '#00ff9d' : sentiment.portfolio_score < -0.2 ? '#ff4d4d' : '#fbbf24' }}>
                  {sentiment.portfolio_score > 0 ? '+' : ''}{sentiment.portfolio_score.toFixed(2)}
                </div>
              </div>
            </div>
            <SentimentBar score={sentiment.portfolio_score} />
          </div>

          <div className="sentiment-grid">
            {sentiment.stocks.map(stock => (
              <div key={stock.ticker} className="glass-card" style={{ cursor: 'pointer', transition: 'border-color 0.2s', borderLeft: `3px solid ${stock.overall_sentiment === 'bullish' ? '#00ff9d' : stock.overall_sentiment === 'bearish' ? '#ff4d4d' : '#fbbf24'}` }}
                onClick={() => setExpandedTicker(expandedTicker === stock.ticker ? null : stock.ticker)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <span style={{ fontSize: 16, fontWeight: 700, color: '#f1f5f9' }}>{stock.ticker}</span>
                    <span style={{ marginLeft: 8, fontSize: 12, color: '#64748b' }}>{stock.analyzed_count} articles</span>
                  </div>
                  <SentimentBadge sentiment={stock.overall_sentiment} />
                </div>
                <SentimentBar score={stock.avg_score} />

                {expandedTicker === stock.ticker && (
                  <div style={{ marginTop: 12, borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 12 }}>
                    {stock.headlines.map((h, i) => (
                      <div key={i} style={{ padding: '8px 0', borderBottom: i < stock.headlines.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                          <p style={{ margin: 0, color: '#cbd5e1', fontSize: 13, lineHeight: 1.5, flex: 1 }}>{h.title}</p>
                          <span style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: h.score > 0.2 ? '#00ff9d' : h.score < -0.2 ? '#ff4d4d' : '#fbbf24',
                            whiteSpace: 'nowrap',
                          }}>
                            {h.score > 0 ? '+' : ''}{h.score.toFixed(2)}
                          </span>
                        </div>
                        <span style={{ fontSize: 11, color: '#64748b', marginTop: 2, display: 'inline-block' }}>{h.source}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {brief && (
        <div style={{ marginTop: 24 }}>
          <div className="insight-card">
            <h2 style={{ color: '#f1f5f9', marginTop: 0 }}>Morning Brief — {today}</h2>
          </div>

          <div className="form-row" style={{ marginTop: 16 }}>
            <div className="glass-card">
              <h3>NASDAQ (QQQ)</h3>
              {brief.nasdaq ? (
                <>
                  <div className="metric-card" style={{ marginTop: 12, marginBottom: 12 }}>
                    <div className="metric-label">QQQ Price</div>
                    <div className="metric-value">${brief.nasdaq.price.toFixed(2)}</div>
                    <div className={`metric-delta ${brief.nasdaq.change_pct >= 0 ? 'gain' : 'loss'}`}>
                      {brief.nasdaq.change_pct >= 0 ? '+' : ''}{brief.nasdaq.change_pct.toFixed(2)}%
                    </div>
                  </div>
                  <p style={{ color: '#94a3b8', lineHeight: 1.6 }}>
                    {brief.nasdaq.change_pct >= 0 ? '🟢' : '🔴'} NASDAQ opened in <strong>{brief.nasdaq.change_pct >= 0 ? 'positive' : 'risk-off'}</strong> territory.
                    Tech mega-caps are {brief.nasdaq.change_pct >= 0 ? 'leading' : 'lagging'} the move.
                    Watch Fed speakers and macro data releases today.
                  </p>
                </>
              ) : <p className="alert-box alert-info">NASDAQ data unavailable right now.</p>}
            </div>

            <div className="glass-card">
              <h3>TSX (XIU.TO)</h3>
              {brief.tsx ? (
                <>
                  <div className="metric-card" style={{ marginTop: 12, marginBottom: 12 }}>
                    <div className="metric-label">XIU.TO Price</div>
                    <div className="metric-value">C${brief.tsx.price.toFixed(2)}</div>
                    <div className={`metric-delta ${brief.tsx.change_pct >= 0 ? 'gain' : 'loss'}`}>
                      {brief.tsx.change_pct >= 0 ? '+' : ''}{brief.tsx.change_pct.toFixed(2)}%
                    </div>
                  </div>
                  <p style={{ color: '#94a3b8', lineHeight: 1.6 }}>
                    {brief.tsx.change_pct >= 0 ? '🟢' : '🔴'} TSX sentiment is <strong>{brief.tsx.change_pct >= 0 ? 'bullish' : 'cautious'}</strong> today.
                    Energy and financials are the key movers.
                    Bank of Canada watchers: next rate decision is closely tracked.
                  </p>
                </>
              ) : <p className="alert-box alert-info">TSX data unavailable right now.</p>}
            </div>
          </div>

          <hr />

          <h3>Stocks to Watch Today</h3>
          <div className="glass-card" style={{ marginTop: 8 }}>
            {[
              ['AAPL', 'Apple — product cycle & services revenue in focus'],
              ['NVDA', 'NVIDIA — AI chip demand remains a key theme'],
              ['SHOP.TO', 'Shopify — e-commerce growth vs. margin story'],
              ['TD.TO', 'TD Bank — North American banking exposure'],
              ['MSFT', 'Microsoft — Azure growth trajectory'],
              ['CNQ.TO', 'CNQ — oil sands production & dividend reliability'],
            ].map(([tick, note]) => (
              <div key={tick} style={{ padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.04)', color: '#cbd5e1' }}>
                <strong>{tick}</strong> — {note}
              </div>
            ))}
          </div>

          <hr />

          <h3>Personalized Note for Your Portfolio</h3>
          {tickers.length > 0 ? (
            <div className="alert-box alert-success" style={{ marginTop: 8 }}>
              You hold: <strong>{tickers.join(', ')}</strong>.
              Given today's market conditions, pay particular attention to macro catalysts
              that affect {tickers.some(t => !t.endsWith('.TO')) ? 'US growth stocks' : ''}
              {tickers.some(t => !t.endsWith('.TO')) && tickers.some(t => t.endsWith('.TO')) ? ' and ' : ''}
              {tickers.some(t => t.endsWith('.TO')) ? 'Canadian equities (CAD/USD rate, BoC policy)' : ''}.
              Your portfolio has <strong>{tickers.length} holding(s)</strong> — {tickers.length >= 5 ? 'well diversified' : 'consider diversifying further'}.
            </div>
          ) : (
            <div className="alert-box alert-info" style={{ marginTop: 8 }}>Add holdings in Portfolio Manager to get a personalized note!</div>
          )}

          <hr />

          <h3>Key Events This Week</h3>
          <div className="glass-card" style={{ marginTop: 8 }}>
            {[
              'US CPI data release — watch for inflation surprises',
              'Fed minutes publication — rate path clarity expected',
              'Major earnings: check your holdings\' reporting dates',
              'Canada employment report — CAD volatility likely',
              'Options expiration Friday — elevated volatility possible',
            ].map((e, i) => (
              <div key={i} style={{ padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.04)', color: '#cbd5e1' }}>
                {e}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default DailyInsights;
